import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { catchError, from, fromEvent, mergeMap, Observable, of, take, tap } from 'rxjs';

import { FormState } from '@app/data/models';
import { RestError } from '@azure/storage-blob';
import { fromByteArray } from 'base64-js';
import timezones, { Timezone } from 'timezones.json';

import { BlobContainer, DayOfWeek, ErrorCodes, LinkType, NodeStatus, NodeType, RegistrationMode, UserRoles } from './enums';
import { Environment } from './models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MEGABYTE_1 } from './constants';
import { FingerprintjsProAngularModule, FpjsClientOptions } from '@fingerprintjs/fingerprintjs-pro-angular';
import { EnvironmentProviders, Provider } from '@angular/core';
import { APP_AUTOUPDATE, APP_DEBUG, APP_DOCUMENT, APP_ENVIRONMENT, APP_INITIAL_POINTER_EVENT, APP_WINDOW } from './tokens';

declare const MediaRecorder: any;

export const LOADING_SPINNER =
    'data:image/gif;base64,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';

export function isOldStorageUrl(value: string): boolean {
    return value.includes('storageazureshare') || value.includes('storageaccountacces94c0');
}

export function hasAny(value: ArrayLike<any> | null | undefined): boolean {
    return Array.isArray(value) && value.length > 0;
}

export function migrateToStorageUrl(
    value: string,
    env: Environment,
    type: 'image' | 'video' | 'html' | 'audio' | 'file' | '' = '',
): string {
    try {
        if (isOldStorageUrl(value)) {
            const uri = new URL(value);
            const storageUri = new URL(env.destinationStorageSasUri);
            const newStorageUrl = `${storageUri.origin}/${env.privateContainerName}`;
            const pathParts = uri.pathname
                .split('/')
                .filter(p => p.length > 0)
                .reduce((result, part, ix) => {
                    if (ix !== 0) {
                        result.push(part);
                    }
                    return result;
                }, [] as string[]);
            const path = pathParts.join('/');

            switch (type) {
                case 'image':
                    return `${newStorageUrl}/rte/images/${path}`;
                case 'video':
                    return `${newStorageUrl}/rte/videos/${path}`;
                case 'audio':
                    return `${newStorageUrl}/rte/audios/${path}`;
                case 'html':
                    return `${newStorageUrl}/rte/html/${path}`;
                case 'file':
                    return `${newStorageUrl}/rte/files/${path}`;
            }

            return `${storageUri.origin}${uri.pathname}${uri.search}`;
        }
    } catch (error) {}

    return value;
}

export function isIos(deviceService: DeviceDetectorService): boolean {
    const os = (deviceService.getDeviceInfo().os || '').toLocaleLowerCase();
    const mobileOs = ['ios', 'iphone', 'ipad', 'ipod'];
    return deviceService.isMobile() && mobileOs.includes(os);
}

export function isSafari(deviceService: DeviceDetectorService): boolean {
    const browser = (deviceService.getDeviceInfo().browser || '').toLocaleLowerCase();
    return browser === 'safari';
}

export function isIosOrSafari(deviceService: DeviceDetectorService): boolean {
    return isIos(deviceService) || isSafari(deviceService);
}

export function buildMediaAssetUrl(mediaAssetId: string, env: Environment) {
    return `${env.cdnUrl}/${env.privateContainerName}/media-assets/${mediaAssetId}/playlist.m3u8`;
}

export function buildProxyUrlForIos(src: string, env: Environment, token: string): string {
    return `${env.serverUrl}/assets/manifest?v=v1&playbackUrl=${encodeURIComponent(src)}&token=${encodeURIComponent(token)}`;
}

const encoder = new TextEncoder();
export function transformToWatermarkUrl(
    value: {
        text: string;
        width: number;
        height: number;
        opacity?: number;
        scale: number;
        fontSize?: number;
        quality?: number;
        wordSpacing?: number;
        lineHeight?: number;
    },
    env: Environment,
): string {
    const bytes = encoder.encode(JSON.stringify(value));
    let base64 = btoa(String.fromCharCode(...new Uint8Array(bytes)));
    const urlSafeBase64 = base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

    return `${env.serverUrl}/watermark/${urlSafeBase64}?v=v1`;
}

export function transformToCdnUrl(
    value: string,
    env: Environment,
    type: 'image' | 'video' | 'html' | 'audio' | 'file' | '' = '',
    useProxy: boolean | ((uri: string) => boolean) = false,
): string {
    if (value.includes('.blob.core.windows.net')) {
        const uri = new URL(migrateToStorageUrl(value, env, type));
        let cdnUri = env.cdnUrl;

        if (useProxy === true || (typeof useProxy === 'function' && useProxy(uri.toString()))) {
            cdnUri = `${env.appUrl}/cdn`;
        }

        return `${cdnUri}${uri.pathname}${uri.search}`;
    } else if (
        (value.startsWith(env.destinationStorageSasUri) || value.startsWith(env.cdnUrl)) &&
        (useProxy === true || (typeof useProxy === 'function' && useProxy(value)))
    ) {
        const uri = new URL(value);
        return `${env.appUrl}/cdn${uri.pathname}${uri.search}`;
    }

    return value;
}

export function getTimeZoneByIanaTimeZone(timeZone: string): Timezone | null {
    return (
        timezones.find(tz => {
            if (tz.utc.length === 0) {
                return false;
            }
            return tz.utc.includes(timeZone);
        }) || null
    );
}

export function formatFileSize(size: number): string {
    if (size < 1024) {
        return size + ' B';
    } else if (size < 1048576) {
        return (size / 1024).toFixed(2) + ' KB';
    } else if (size < 1073741824) {
        return (size / 1048576).toFixed(2) + ' MB';
    } else {
        return (size / 1073741824).toFixed(2) + ' GB';
    }
}

export function formatLinkUrl(model: { rowKey: string | null; url: string; type: LinkType }, env: Environment): string {
    let domain = '';
    const isProd = env.production;

    switch (model.type) {
        case LinkType.Shareable:
            if (model.rowKey === 'linkrequest') {
                domain = isProd ? 'https://shareablelink.online/r' : `http://localhost:4304/r`;
            } else {
                domain = isProd ? 'https://shareablelink.online/l' : `http://localhost:4301/l`;
            }
            break;
        case LinkType.Form:
            domain = isProd ? 'https://shareablelink.online/f' : `http://localhost:4302/f`;
            break;
        case LinkType.Event:
            domain = isProd ? 'https://shareablelink.online/e' : `http://localhost:4303/e`;
            break;
        case LinkType.ChecklistLink:
            domain = isProd ? 'https://shareablelink.online/c' : `http://localhost:4305/c`;
            break;
        case LinkType.Guest:
            domain = isProd ? 'https://shareablelink.online/g' : `http://localhost:4306/g`;
            break;
    }

    return `${domain}/${model.url}`;
}

export function readAsArrayBuffer(file: File | Blob): Observable<ArrayBuffer> {
    return new Observable(subscriber => {
        const reader = new FileReader();
        reader.onerror = err => {
            subscriber.error(err);
            subscriber.complete();
        };
        reader.onload = () => {
            subscriber.next(new Uint8Array(reader.result as ArrayBuffer));
            subscriber.complete();
        };
        reader.readAsArrayBuffer(file);
    });
}

export function formatRegistrationKey(date: Date): string {
    return `${date.getFullYear().toString().padStart(2, '0')}${(date.getMonth() + 1).toString().padStart(2, '0')}${date
        .getDate()
        .toString()
        .padStart(2, '0')}`;
}

export function isFormValid(form: FormState<any>): boolean {
    return form.status === 'VALID';
}

export function dhms(totalSeconds: number): number[] {
    const days = Math.floor(totalSeconds / 86400);
    totalSeconds -= days * 86400;
    const hours = Math.floor(totalSeconds / 3600) % 24;
    totalSeconds -= hours * 3600;
    const minutes = Math.floor(totalSeconds / 60) % 60;
    totalSeconds -= minutes * 60;
    const seconds = totalSeconds % 60;

    return [days, hours, minutes, seconds];
}

export function getPath(
    node: { path: string; rowKey: string | null; title: string },
    nodes: { path: string; rowKey: string | null; title: string }[],
): string[] {
    if (node.path) {
        const paths = node.path.split('.');
        return paths.reduce((result, rowKey) => {
            const n = nodes.find(n => n.rowKey === rowKey);

            if (n) {
                result.push(n.title);
            }

            return result;
        }, [] as string[]);
    }

    return [];
}

export const provideDefaultProviders = (env: Environment) => {
    return [
        { provide: APP_ENVIRONMENT, useValue: env },
        {
            provide: APP_AUTOUPDATE,
            useFactory: (env: Environment) => {
                return env.autoUpdate;
            },
            deps: [APP_ENVIRONMENT],
        },
        {
            provide: APP_INITIAL_POINTER_EVENT,
            useFactory: () => {
                return () => (window as any).hasPointerEventHappened === true;
            },
        },
        {
            provide: APP_DEBUG,
            useFactory: (env: Environment) => {
                return env.debug;
            },
            deps: [APP_ENVIRONMENT],
        },
        { provide: APP_WINDOW, useFactory: () => window },
        { provide: APP_DOCUMENT, useFactory: () => document },
    ];
};

export const provideFingerprintjsPro = (clientOptions: FpjsClientOptions) => {
    const FPJS_PROVIDERS = FingerprintjsProAngularModule.forRoot(clientOptions).providers as (
        | Provider
        | EnvironmentProviders
    )[];

    return FPJS_PROVIDERS;
};

export function isMimeTypeSupported(mimeType: string) {
    if (typeof MediaRecorder === 'undefined') {
        return false;
    }

    if (typeof MediaRecorder.isTypeSupported !== 'function') {
        return true;
    }

    return MediaRecorder.isTypeSupported(mimeType);
}

export function replaceIllegalCharacters(name: string): string {
    return name.replace(/[^\x00-\x7F]/g, '').replace(/[<>%&:\\?/+*']/g, '');
}

export function toDateTimeString(date: Date): string {
    const year = date.getFullYear().toString();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`.trim();
}

export function isDst(date?: Date): boolean {
    const now = date ?? new Date();
    const start = new Date(now.getFullYear(), 2, 7, 2); // march
    start.setDate(7 + (7 - start.getDay()));
    const end = new Date(now.getFullYear(), 10, 7, 2); // november
    end.setDate(7 - end.getDay());

    return now < start || now > end;
}

export function parseTimeZoneFromOffset(offset: number): string {
    const hours = Math.abs(offset).toString().padStart(2, '0');
    const minutes = Math.abs(parseInt((60 * (offset % 1)).toString(), 10))
        .toString()
        .padStart(2, '0');
    return offset < 0 ? `-${hours}${minutes}` : `+${hours}${minutes}`;
}

export function getTimeZoneOffset(timezone: { isdst: boolean; offset: number }, date: Date): number {
    if (timezone.isdst && isDst(date)) {
        return timezone.offset + (timezone.offset < 0 ? -1 : 1);
    }

    return timezone.offset;
}

export function getUtcDate(date: string | Date, timeZone: Timezone): Date {
    return new Date(getUtcDateString(date, timeZone));
}

export function getUtcDateString(date: string | Date, timeZone: Timezone): string {
    if (isString(date)) {
        return `${date.replace(' ', 'T')}${parseTimeZoneFromOffset(getOffset(timeZone, new Date(date.replace(' ', 'T'))))}`;
    }

    if (isDate(date)) {
        return `${toDateTimeString(date)}${parseTimeZoneFromOffset(getOffset(timeZone, date))}`;
    }

    throw new Error('Invalid type');
}

export function getOffset(timezone: Timezone, date: Date): number {
    return timezone.offset;
    // const year = date.getFullYear();
    // const month = (date.getMonth() + 1).toString().padStart(2, '0');
    // const day = date.getDate().toString().padStart(2, '0');

    // const midnight = `${year}-${month}-${day}T00:00:00Z`;

    // const midnightUtc = new Date(midnight);

    // const midnightAsTimeZone = midnightUtc.toLocaleString('en-US', { timeZone: timezone.utc[0] });
    // const timeZoneDate = new Date(midnightAsTimeZone);

    // const hours = timeZoneDate.getHours();
    // const min = timeZoneDate.getMinutes();

    // const hours2 = midnightUtc.getHours();
    // console.log(hours, hours2);

    // // comparison problem
    // // we can't compare the local timezone to utc midnight timezone
    // if (timeZoneDate > midnightUtc) {
    //     return hours + (min === 30 ? 0.5 : 0);
    // } else {
    //     return hours - 24 + (min === 30 ? 0.5 : 0);
    // }
}

export function readAsText(file: File | Blob): Observable<string> {
    return new Observable(subscriber => {
        const reader = new FileReader();
        reader.onerror = err => {
            subscriber.error(err);
            subscriber.complete();
        };
        reader.onload = () => {
            subscriber.next(reader.result as string);
            subscriber.complete();
        };
        reader.readAsText(file);
    });
}

export function base64ToBlob(data: any, contentType = '', sliceSize = 512) {
    const byteCharacters = atob(data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
}

export function sortNodes(
    a: { type: number; colour: number; title: string; status: NodeStatus },
    b: { type: number; colour: number; title: string; status: NodeStatus },
) {
    const aTitle = (a.title || '').replace("'", ' ');
    const bTitle = (b.title || '').replace("'", ' ');

    if (a.status !== b.status) {
        return b.status - a.status;
    } else if (a.type === b.type) {
        if (a.type === NodeType.Node) {
            const colour = +a.colour - +b.colour;
            return colour === 0 ? aTitle.localeCompare(bTitle) : colour;
        }

        return aTitle.localeCompare(bTitle);
    } else {
        if (a.type === NodeType.Node) {
            return -1;
        }
        if (b.type === NodeType.Node) {
            return 1;
        }
        if (a.type === NodeType.Video) {
            return -1;
        }
        if (b.type === NodeType.Video) {
            return 1;
        }
        if (a.type === NodeType.Audio) {
            return -1;
        }
        if (b.type === NodeType.Audio) {
            return 1;
        }
        if (a.type === NodeType.Html) {
            return -1;
        }
        if (b.type === NodeType.Html) {
            return 1;
        }
        if (a.type === NodeType.Text) {
            return -1;
        }
    }

    return aTitle.localeCompare(bTitle);
}

export function parseJwt(token: string): any {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
        atob(base64)
            .split('')
            .map(function (c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            })
            .join(''),
    );

    return JSON.parse(jsonPayload);
}

export function getActivatedRoute(state: RouterStateSnapshot): ActivatedRouteSnapshot {
    let child = state.root;

    while (child.firstChild) {
        child = child.firstChild;
    }

    return child;
}

export async function handleFileUpload(
    serverUrl: string,
    token: string,
    file: File,
    progressFn?: (e: ProgressEvent, isComplete: boolean) => void,
    isPublic: boolean = false,
): Promise<string> {
    return new Promise((resolve, reject) => {
        if (file === null) {
            return reject();
        }

        const { type, size } = file;

        const reader: FileReader = new FileReader();

        reader.onerror = err => reject(err);

        fromEvent(reader, 'loadend')
            .pipe(
                take(1),
                mergeMap(() => {
                    const blob = new Blob([reader.result as ArrayBuffer], { type });
                    const uri = URL.createObjectURL(blob);

                    return of(uri);
                }),
                mergeMap(src =>
                    from(
                        uploadFile(
                            serverUrl,
                            src,
                            token,
                            file.name,
                            type === 'video/quicktime' ? 'application/octet-stream; charset=UTF-8' : null,
                            progressFn,
                            isPublic,
                        ),
                    ),
                ),
                tap(data => resolve(data)),
                catchError(err => {
                    reject(err);
                    return of(null);
                }),
            )
            .subscribe();

        reader.readAsArrayBuffer(file);
    });
}

export async function prepareImageForUpload(file: File, compressThreshold: boolean | number = MEGABYTE_1): Promise<string> {
    return new Promise((resolve, reject) => {
        if (file === null) {
            return reject();
        }

        const { type, size } = file;
        const reader: FileReader = new FileReader();

        reader.onerror = err => reject(err);

        fromEvent(reader, 'loadend')
            .pipe(
                take(1),
                mergeMap(() => {
                    const base64 = fromByteArray(new Uint8Array(reader.result as ArrayBuffer));
                    const src = `data:${type};base64,${base64}`;

                    if (compressThreshold === false || (isNumber(compressThreshold) && size < compressThreshold)) {
                        return of(src);
                    }

                    const img = new Image();
                    img.src = src;

                    return fromEvent(img, 'load').pipe(
                        take(1),
                        mergeMap(() => {
                            return from(compressImage(img, 0.3, img.width, img.height));
                        }),
                    );
                }),
                tap(data => resolve(data)),
                catchError(err => {
                    reject(err);
                    return of(null);
                }),
            )
            .subscribe();

        reader.readAsArrayBuffer(file);
    });
}

export function compressImage(image: HTMLImageElement, quality: number, width: number, height: number): Promise<string> {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(image, 0, 0, width, height);

        canvas.toBlob(
            blob => {
                if (blob) {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = () => {
                        const base64data = reader.result as string;
                        resolve(base64data);
                        // this.editor.chain().setImage({ src: base64data }).run();
                    };
                    return;
                }

                reject();
            },
            'image/jpeg',
            quality,
        );
    });
}

export type ProgressFn = (event: ProgressEvent, isComplete: boolean) => void;

export async function uploadFile(
    serverUrl: string,
    src: string,
    token: string,
    filename?: string,
    contentType: string | null = null,
    progressFn?: ProgressFn,
    isPublic = false,
): Promise<string> {
    const result = await fetch(src);

    const type = contentType || (result.headers.get('Content-Type') as string);
    const file = new File([await result.arrayBuffer()], filename || 'file', { type });

    const formData = new FormData();
    formData.append('isPublic', isPublic.toString());
    formData.append('data', file);
    formData.append('contentType', type);

    const response = await makeRequest('POST', `${serverUrl}/file/rte/upload`, formData, token, progressFn);

    try {
        const json = JSON.parse(response as string);
        return json.data;
    } catch (error) {
        console.error(error);
        throw error;
    }
}

export function makeRequest(method: string, url: string, body: any, token: string, progressFn?: ProgressFn) {
    return new Promise(function (resolve, reject) {
        const xhr = new XMLHttpRequest();

        xhr.open(method, url);
        xhr.onload = function () {
            if (this.status >= 200 && this.status < 300) {
                resolve(xhr.response);
            } else {
                reject({
                    status: this.status,
                    statusText: xhr.statusText,
                });
            }
        };
        xhr.onerror = function () {
            reject({
                status: this.status,
                statusText: xhr.statusText,
            });
        };

        xhr.upload.addEventListener('progress', e => {
            if (progressFn) {
                progressFn(e, false);
            }
        });

        xhr.setRequestHeader('Authorization', `Bearer ${token}`);

        xhr.send(body);
    });
}

export function randomId(length: number): string {
    let result: string | null = null;
    const allowed = /^[a-zA-Z0-9]{1,}$/;

    do {
        const bytes = Array.from({ length }, () => Math.floor(Math.random() * 255));
        const array = Uint8Array.from(bytes);
        result = fromByteArray(array);
    } while (result === null || allowed.test(result) === false);

    return result;
}

// export function randomId(size: number) {
//     return [...Array(size)].map(() => Math.floor(Math.random() * 16).toString(16)).join('');
// }

export function containsPath(a: string, b: string): boolean {
    const nodePath = (a || '')
        .split('.')
        .filter(s => s !== 'null')
        .join('.');
    const path = (b || '')
        .split('.')
        .filter(s => s !== 'null')
        .join('.');

    return nodePath.startsWith(path);
}

export function getErrorCodeMessage(code: ErrorCodes, meta?: { [key: string]: string | number | string[] }): string {
    const username = meta?.['username'] || null;

    switch (code) {
        case ErrorCodes.IndexUpdate:
            return `Error Code (${code}): Error updating index`;
        // 100
        case ErrorCodes.InvalidPassword:
            return `Error Code (${code}): Incorrect password`;
        case ErrorCodes.TfaUnavailable:
            return `Error Code (${code}): TFA is not available at this time. Please try again later.`;
        case ErrorCodes.TfaInvalidCode:
            return `Error Code (${code}): TFA code is has expired or is invalid. Please try again.`;

        // 200
        case ErrorCodes.DuplicateEmails:
            return `Error Code (${code}): Multiple users contain duplicate emails addresses`;
        case ErrorCodes.ExistingEmails:
            return `Error Code (${code}): User(s) email addresses already existing for another user(s)`;
        case ErrorCodes.OwnerRoleChange:
            return `Error Code (${code}): You cannot modify your account role`;
        case ErrorCodes.OwnerPromotion:
            return `Error Code (${code}): You cannot promote users to the owner role`;
        case ErrorCodes.OwnerDemotion:
            return `Error Code (${code}): You cannot demote users from the owner role`;
        case ErrorCodes.Crossline:
            return `Error Code (${code}): You cannot modify users outside of your organization`;
        case ErrorCodes.InvalidOrganizationId:
            return `Error Code (${code}): Invalid organization Id`;
        case ErrorCodes.InvalidNodeId:
            return `Error Code (${code}): Invalid node Id`;
        case ErrorCodes.OwnerNodeChange:
            return `Error Code (${code}): You cannot modify user permissions outside of your organization`;
        case ErrorCodes.ValidationError: {
            const results: string[] = (meta?.['results'] as string[]) || [];
            return `Error Code (${code}): Validation failed for the following reasons: ${results.join(',')}`;
        }
        case ErrorCodes.OwnerInvalidOrganizationId:
            return `Error Code (${code}): One or more organization owners are missing the required organiztion id:`;

        // 300
        case ErrorCodes.AssetNotFound:
            return `Error Code (${code}): Asset could not be found`;
        case ErrorCodes.OrganizationNotFound:
            return `Error Code (${code}): Organization could not be found`;
        case ErrorCodes.UserNotFound:
            return `Error Code (${code}): User could not be found`;
        case ErrorCodes.NodeNotFound:
            return `Error Code (${code}): Node could not be found`;

        // 400
        case ErrorCodes.InvalidToken:
            return `Error Code (${code}): Invalid security token`;

        // 500
        case ErrorCodes.InvalidBinary:
            return `Error Code (${code}): An error has occured while attempting to retrieve this binary. Please try again later.`;
        case ErrorCodes.AssetPublishing:
            return `Error Code (${code}): An error has occured while attempting to setup the video stream. Please try again later.`;
        case ErrorCodes.InvalidNodeIdForContent:
            return `Error Code (${code}): You do not have permission to view the content.`;
        case ErrorCodes.InvalidAssetState:
            return `Error Code (${code}): This media is currently still processing. Please try again later when it is finished.`;

        // 600
        case ErrorCodes.NoConnection:
            return `Error Code (${code}): Connection could not be established. Please check your internet connection.`;
        case ErrorCodes.NormailizeAudio:
            return `Error Code (${code}): An error has occurred while attempting to process the audio recording. Please try again later.`;
        case ErrorCodes.CookieTestFailed:
            return `Error Code (${code}): Cookies must be enabled to proceed.`;

        // 700
        case ErrorCodes.LinkNotFound:
            return `Error Code (${code}): Link could not be found`;
        case ErrorCodes.LinkSessionLimit:
            return `Error Code (${code}): The user capacity for this link has been reached. If you already have access on a different device & browser, please return to that device & browser`;
        case ErrorCodes.LinkExpired:
            return `Error Code (${code}): Link has expired`;
        case ErrorCodes.LinkHasNotStarted:
            return `Error Code (${code}): Link has not started yet`;
        case ErrorCodes.LinkSessionInactive: {
            const linkUrl = meta?.['linkId'] || '';
            return `Error Code (${code}): This Device & Browser does not have access to the link '${linkUrl}'`;
        }
        case ErrorCodes.LinkSubmissionExists:
            return `Error Code (${code}): Registration already exists`;
        case ErrorCodes.LinkSubmissionInvalid:
            return `Error Code (${code}): Incomplete Registration`;
        case ErrorCodes.LinkRequiredFieldMissing:
            return `Error Code (${code}): Required field missing`;
        case ErrorCodes.LinkFieldMustBeUnique: {
            const field = meta?.['Field'] || meta?.['question'];
            return `Error Code (${code}): Form question '${field}' has already been previously submitted`;
        }
        case ErrorCodes.LinkRegistrationEnded:
            return `Error Code (${code}): Registration for this link has closed. If you missed the deadline by mistake, please speak with whomever shared the link with you. If you have already registered for this link, please ensure that you are using the original device and browser that you registered with.`;
        case ErrorCodes.LinkSubmissionApproved: {
            if (username) {
                return `Registration submission was approved by ${username}`;
            }

            return `Registration submission was approved`;
        }
        case ErrorCodes.LinkSubmissionPending:
            return `Registration submission has been received and is pending approval`;
        case ErrorCodes.LinkSessionMerged: {
            if (username) {
                return `User was merged by ${username}`;
            }
            return `User was merged`;
        }
        case ErrorCodes.LinkSessionRegistrationPermitted:
            return `User was permitted to register after the cut-off`;
        case ErrorCodes.LinkSessionActivated: {
            if (username) {
                return `User session was activated by ${username}`;
            }

            return `User session was activated by admin`;
        }
        case ErrorCodes.LinkSessionDeactivated: {
            if (username) {
                return `User session was deactivated by ${username}`;
            }

            return `User session was deactivated by admin`;
        }
        case ErrorCodes.LinkSubmissionAutoApproved:
            return `Registration submission has been received and was auto-approved`;
        case ErrorCodes.LinkNoRegistration:
            return `No registration required. Access granted`;
        case ErrorCodes.LinkOneTimeAccess:
            return `Error Code (${code}): One-time access limit has been reached`;
        case ErrorCodes.LinkSessionReset:
            return `Session was reset`;
        case ErrorCodes.LinkSessionTimeReset:
            return `Session activation date was reset`;
        case ErrorCodes.LinkSessionExtension:
            const days = meta?.['days'] || null;

            if (days && username) {
                return `Session was extended ${days} days by ${username}`;
            }

            return `Session was extended`;
        case ErrorCodes.LinkSessionNameChanged: {
            const previous = meta?.['previous'];
            const current = meta?.['current'];
            return `User changed their name from '${previous}' to '${current}'`;
        }
        case ErrorCodes.LinkSessionRequestMoreTime:
            return `User has requested more time`;

        // 900
        case ErrorCodes.SubmissionInvalid:
            return `Error Code (${code}): Submission Invalid`;
        case ErrorCodes.SubmissionComplete:
            return `Submission has been completed`;
        case ErrorCodes.SubmissionSubmitted:
            return `Submission has been submitted`;
        // case ErrorCodes.SubmissionFormLogicInvalid: // not used
        //     return `Submission invalid`;
        case ErrorCodes.SubmissionFinalized:
            return `Submission was finalized by admin`;

        // 1000
        case ErrorCodes.ZoomApiError: {
            const zoomApiError = meta?.['zoomErrorCode'] || null;

            switch (zoomApiError) {
                case 3027:
                    return `Error occurred with Zoom. Host may not register`;
                default:
                    return `Error occurred with Zoom. Check Zoom meeting start and end times`;
            }
        }

        case ErrorCodes.ZoomAddRegistrantError:
            return `Error adding registrant to webinar. Please try again later.`;
        case ErrorCodes.ZoomTooManyRequestsAddRegistrant:
            return `Too many requests to add registrant. Please try again later or try using a different email address.`;
        case ErrorCodes.ZoomWebinarNotFound:
            return `Webinar was not found`;
        case ErrorCodes.ZoomWebinarHasEnded:
            return `Webinar has ended`;
        case ErrorCodes.ZoomRegistrantAlreadyExists:
            return `A registrant with this email already exists. Please try a different email address`;
        case ErrorCodes.ZoomWebinarMustBeFutureDate:
            return `Webinar must be a future date`;
        case ErrorCodes.ZoomWebinarUpdateFailed: // missing
            return `Error Code (${code}): Zoom update failed. Talk to Macaulay`;

        case ErrorCodes.FastlyApiError: // missing
            return `Error Code (${code}): Fastly update failed. Talk to Macaulay`;

        default:
            return `Error Code (${code}): An error has occured. Please try again later.`;
    }
}

export function getHttpErrorMessage(response: HttpErrorResponse) {
    switch (response.status) {
        case 504:
            return 'Gateway Timeout Occurred. Please check your connection and try again later.';
        case 0:
            return 'Could not communicate with server. Please check your connection.';
        default:
            return `(${response.status}) ${response.statusText}`;
    }
}

export function getRestErrorMessage(error: RestError): string {
    switch (error.statusCode) {
        case 404:
            return `Error Code: ${error.statusCode} Resource Not Found`;
        default: {
            if (error.details) {
                const details = (error.details as any) || null;
                return `Error Code: ${error.statusCode} ${details?.errorCode}`.trim();
            }
        }
    }

    return error.message;
}

export function getMediaErrorMessage(value: MediaError): string {
    switch (value.code) {
        case MediaError.MEDIA_ERR_ABORTED:
            return 'Media Aborted Error';
        case MediaError.MEDIA_ERR_DECODE:
            return 'Media Decode Error';
        case MediaError.MEDIA_ERR_NETWORK:
            return 'Media Network Error';
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            return 'Media Not Supported';
        default:
            return `Media Error: ${value.message}`;
    }
}

export function getTextContent(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent ?? '';
}

export function hasContent(value: string | null | undefined, isHtml: boolean = false): boolean {
    if (!value) {
        return false;
    }

    if (isHtml) {
        const div = document.createElement('div');
        div.innerHTML = value;
        return (div.textContent ?? '').trim().length > 0 || div.querySelector('img,video,audio,media-asset,div[data-type]') !== null;
    }

    return !isNullOrEmpty(value);
}

export function isHttpError(model: any): model is HttpErrorResponse {
    //return model instanceof HttpErrorResponse; // doesn't work because it's referenced from a separate npm library
    return model?.constructor?.name === 'HttpErrorResponse';
}

export function isRestError(model: any): model is RestError {
    return model instanceof RestError;
}

export const DEFAULT_TIME_ZONE = new Intl.DateTimeFormat('en-US').resolvedOptions().timeZone;

export function todayAsString(now = new Date(), delimiter = '-'): string {
    const year = now.getFullYear().toString();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');

    return `${year}${delimiter}${month}${delimiter}${day}`;
}

export function formatDate(date: Date, timeZone = DEFAULT_TIME_ZONE, locale = 'en-US'): string {
    return date.toLocaleDateString(locale, { timeZone });
}

export function formatTime(date: Date, timeZone = DEFAULT_TIME_ZONE, locale = 'en-US'): string {
    return date.toLocaleTimeString(locale, { timeZone });
}

export function formatDatetime(date: Date, locale = 'en-US'): string {
    return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    }).format(date);
}

export function formatDatetimeWithTimeZone(date: Date, timeZone: Timezone | string, locale = 'en-US'): string {
    if (typeof timeZone === 'string') {
        return new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            timeZone,
            timeZoneName: 'short',
        }).format(date);
    }

    return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZone: timeZone.utc[0],
        timeZoneName: 'short',
    }).format(date);
}

export function isValidDate(model: Date): boolean {
    return model instanceof Date && !isNaN(model.valueOf());
}

export function isObject(model: any): model is object {
    return getType(model) === 'object';
}

export function isFunction(model: any): model is Function {
    return getType(model) === 'function';
}

export function isError(model: any): model is Error {
    return getType(model) === 'error';
}

export function isString(model: any): model is string {
    return getType(model) === 'string';
}

export function isBoolean(model: any): model is string {
    return getType(model) === 'boolean';
}

export function isFile(model: any): model is File {
    return getType(model) === 'file';
}

export function isArray<T>(model: any): model is Array<T> {
    return getType(model) === 'array';
}

export function isNumber(model: any): model is number {
    return getType(model) === 'number';
}

export function isDate(model: any): model is Date {
    return getType(model) === 'date';
}

export function isUint8Array(model: any): model is Uint8Array {
    return getType(model) === 'uint8array';
}

export function isBlob(model: any): model is Blob {
    return getType(model) === 'blob';
}

export function isMediaError(model: any): model is MediaError {
    return getType(model) === 'mediaerror';
}

export function isNullOrUndefined(value: any): value is null | undefined {
    return value === null || value === undefined;
}

export function isNullOrEmpty(value: string | null | undefined): value is null | undefined | '' {
    return isNullOrUndefined(value) || value === '';
}

export function isType<T>(model: any, condition: string | ((m: any) => boolean)): model is T {
    if (isString(condition)) {
        return Object.prototype.hasOwnProperty.call(model, condition);
    }

    return condition(model);
}

export function getType(model: any): string {
    const type = {}.toString.call(model);

    switch (type) {
        case '[object Number]':
            return 'number';
        case '[object Date]':
            return 'date';
        case '[object String]':
            return 'string';
        case '[object Null]':
            return 'null';
        case '[object Undefined]':
            return 'undefined';
        case '[object Object]':
            return 'object';
        case '[object Boolean]':
            return 'boolean';
        case '[object Array]':
            return 'array';
        case '[object File]':
            return 'file';
        case '[object Uint8Array]':
            return 'uint8array';
        case '[object Blob]':
            return 'blob';
        case '[object Error]':
            return 'error';
        case '[object MediaError]':
            return 'mediaerror';
        case '[object Function]':
            return 'function';
        default:
            return type;
    }
}

export function isSameDate(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function sortDates(a: Date, b: Date): number {
    return b.getTime() - a.getTime();
}

export function isOwner(role: UserRoles | null | undefined): boolean {
    return role !== null && role !== undefined && (role & (UserRoles.OrganizationOwner | UserRoles.Administrator)) !== 0;
}

export function isAdmin(role: UserRoles | null | undefined): boolean {
    return role !== null && role !== undefined && (role & UserRoles.Administrator) !== 0;
}

export function daysInMonth(year: number, month: number): number {
    return new Date(year, month, 0).getDate();
}

export function getFormSubmissionResetDate(
    model: { registrationMode: RegistrationMode; dayOfWeek: DayOfWeek },
    getStartDate = false,
): Date | null {
    const now = new Date();
    switch (model.registrationMode) {
        case RegistrationMode.Daily:
            return getStartDate
                ? new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
                : new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        case RegistrationMode.Weekly: {
            const daysToAdd = (model.dayOfWeek - now.getDay() + 7) % 7;
            return getStartDate
                ? new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysToAdd - 7, 0, 0, 0)
                : new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysToAdd, 23, 59, 59);
        }
        case RegistrationMode.Monthly: {
            const totalDaysInMonth = daysInMonth(now.getFullYear(), now.getMonth() + 1);
            return getStartDate
                ? new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0)
                : new Date(now.getFullYear(), now.getMonth(), totalDaysInMonth, 23, 59, 59);
        }
    }

    return null;
}

export function generatePassword(len = 8) {
    const passwordChars = '123456789ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz';
    const password = Array(len)
        .fill(passwordChars)
        .map(x => {
            return x[Math.floor(Math.random() * x.length)];
        })
        .join('');

    return password;
}

export function convertObjectKeysToCamelCase(obj: any): any {
    if (Array.isArray(obj)) {
        return obj.map(item => convertObjectKeysToCamelCase(item));
    } else if (obj !== null && typeof obj === 'object') {
        return Object.keys(obj).reduce((acc, key) => {
            const camelKey = pascalToCamelCase(key);
            acc[camelKey] = convertObjectKeysToCamelCase(obj[key]);
            return acc;
        }, {} as any);
    }
    return obj;
}

export function pascalToCamelCase(str: string): string {
    return str.charAt(0).toLowerCase() + str.slice(1);
}

export function getContainerName(env: Environment, containerName: string | BlobContainer = BlobContainer.Private): string {
    if (typeof containerName === 'string') {
        return containerName;
    }

    return containerName === BlobContainer.Private ? env.privateContainerName : env.publicContainerName;
}
